/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\components\Layout.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import AuthContext from '../contexts/AuthContext';
import SoNestingLogo from './SoNestingLogo';

const Layout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const isAdminRoute = location.pathname.startsWith('/admin');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Barre de navigation principale So-nesting */}
      <nav className="gradient-so text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <a 
                  href="/landing" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity duration-200"
                  title="Ouvrir le site So-nesting"
                >
                  <SoNestingLogo size="sm" showText={true} className="text-white" />
                </a>
              </div>
              <div className="hidden md:ml-6 md:flex md:space-x-1">
                <Link
                  to="/"
                  className={`nav-link ${isActive('/') ? 'nav-link-active' : ''}`}
                >
                  📊 Dashboard
                </Link>
                <Link
                  to="/devis"
                  className={`nav-link ${isActive('/devis') ? 'nav-link-active' : ''}`}
                >
                  📋 Devis
                </Link>
                <Link
                  to="/clients"
                  className={`nav-link ${isActive('/clients') ? 'nav-link-active' : ''}`}
                >
                  👥 Clients
                </Link>
                <Link
                  to="/parametres"
                  className={`nav-link ${isActive('/parametres') ? 'nav-link-active' : ''}`}
                >
                  ⚙️ Paramètres
                </Link>
                <Link
                  to="/entreprise"
                  className={`nav-link ${isActive('/entreprise') ? 'nav-link-active' : ''}`}
                >
                  🏢 Entreprise
                </Link>
                <Link
                  to="/abonnement"
                  className={`nav-link ${isActive('/abonnement') ? 'nav-link-active' : ''}`}
                >
                  💳 Abonnement
                </Link>
                {user?.role === 'admin' && (
                  <Link
                    to="/admin"
                    className={`nav-link ${isActive('/admin') ? 'nav-link-active' : ''}`}
                  >
                    🛠️ Administration
                  </Link>
                )}
              </div>
            </div>
            <div className="flex items-center">
              <div className="ml-3 relative">
                <div className="flex items-center space-x-3">
                  <span className="text-white/90 text-sm font-medium">
                    👋 {user?.name || 'Utilisateur'}
                  </span>
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-white/20 text-white/90 border border-white/30">
                    {user?.isBeta ? "Beta" : "Live"}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 backdrop-blur-sm"
                  >
                    🚪 Déconnexion
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Contenu principal */}
      <main className={`${isAdminRoute ? 'max-w-none px-6 lg:px-10' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'} mx-auto py-6`}>
        <Outlet />
      </main>

      {/* Pied de page So-nesting */}
      <footer className="bg-white shadow-inner py-6 mt-8 border-t border-so-beige-light/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-4">
              <SoNestingLogo size="sm" showText={false} />
              <div className="text-so-beige-dark">
                <p className="text-sm font-medium">So-nesting</p>
                <p className="text-xs text-so-beige">Solution d'optimisation calepinage</p>
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="text-so-beige-dark text-sm">
                &copy; {new Date().getFullYear()} So-nesting - Tous droits réservés
              </p>
              <p className="text-so-beige text-xs mt-1">
                Optimisez vos découpes, maximisez vos économies
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
